import { useCallback } from "react";
import { CopyResourceDc, ResourceType, ResourceTypeLink } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { createFilterCopyTask, FilterCopyTask } from "../../../../evergis/core/tools/createFilterCopyTask";
import { TaskDataType } from "../../../../interfaces/scheduler";
import { useResourceType } from "../../../../hooks/useResourceType";
import { tableInfos } from "../../../../resources/tables";
import { getResourceName } from "../../../../utils/resourceName";
import { useAfterCopy } from "../../CopyResource/hooks/useAfterCopy";
import { JobSource, useCopyJobNotifications } from "../../CopyResource/hooks/useCopyJobNotifications";
import { useCopyResources } from "../../CopyResource/hooks/useCopyResources";
import { useSelectedResourcesNames } from "../../CopyResource/hooks/useSelectedResourcesNames";
import { BulkCopyJob } from "../../CopyResource/types";

export const useBulkCopyTablesJob = (): BulkCopyJob => {
  const { t } = useTranslation("dialogs");
  const [resourceType] = useResourceType();
  const resourcesToCopy: string[] = useSelectedResourcesNames();
  const tableInfosInput = resourceType === ResourceTypeLink.Table ? resourcesToCopy : undefined;
  const { refetch, data } = tableInfos.use({ input: tableInfosInput, immediate: false });
  const { copyJob, copyData: jobResult, copyLoading: jobLoading, copyError: jobError } = useCopyResources();
  const tableAlias = (data && data[0] && data[0].alias) || "";
  const notificationInfos = useCopyJobNotifications(JobSource.BulkTables, { alias: tableAlias, jobResult });
  const { setInnerTasks } = useAfterCopy(notificationInfos, { jobResult, jobLoading, jobError });

  const onCopy = useCallback(async () => {
    const result = await refetch();

    if (result === undefined || result.data === undefined) {
      return;
    }

    const tablesToCopy = result.data.map(({ name, alias }) => ({ name, alias, targetName: getResourceName() }));
    const innerTasks: FilterCopyTask<TaskDataType.Table>[] = tablesToCopy.map(({ name, targetName }) =>
      createFilterCopyTask({
        dataStorage: TaskDataType.Table,
        sourceName: name,
        targetName,
      }),
    );
    const tablesInput: CopyResourceDc[] = tablesToCopy.map(
      ({ name, alias, targetName }): CopyResourceDc => ({
        name,
        type: ResourceType.Table,
        copyName: targetName,
        copyAlias: `${alias} ${t("bulkCopy.copy")}`,
        copyWith: null,
      }),
    );

    setInnerTasks(innerTasks);
    await copyJob({ input: tablesInput });
  }, [tableInfosInput, refetch, setInnerTasks, copyJob, t]);

  return { onCopy };
};
